import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import {
  Wrench,
  Users,
  FileText,
  ClipboardList,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Plus,
  CalendarClock,
  Package,
  ArrowRight,
  ChevronRight,
  Mic,
  ListTodo,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/StatusPill";
import { KindIcon, ReportTypeBadge } from "@/components/Badges";
import { EmptyState } from "@/components/EmptyState";
import { BrandMark } from "@/components/Logo";
import { TaskItem } from "@/components/tasks/TaskItem";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { QuickAddTask } from "@/components/tasks/QuickAddTask";
import { useAuth, useFeatures } from "@/context/AuthContext";
import { formatDate, daysText, kindLabel } from "@/lib/format";
import { fetchTasks, useTaskSummary, TASKS_CHANGED } from "@/lib/tasks";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 10) return "God morgon";
  if (h < 18) return "God dag";
  return "God kveld";
};

const StatCard = ({ icon: Icon, label, value, tone, testid, onClick, hint }) => {
  const tones = {
    dark: "bg-[#0B0D10] text-white border-transparent",
    overdue: "status-overdue",
    upcoming: "status-upcoming",
    ok: "status-ok",
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={cn(
        "pressable relative overflow-hidden rounded-[var(--radius)] border p-4 text-left shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:p-5",
        tones[tone]
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider opacity-80 md:text-sm md:normal-case md:tracking-normal">{label}</span>
        <Icon className="h-5 w-5 opacity-80" />
      </div>
      <div className="mt-2 font-heading text-3xl font-extrabold tabular-nums md:text-4xl">{value}</div>
      {hint && <div className="mt-1 text-[11px] opacity-70 md:text-xs">{hint}</div>}
      {tone === "dark" && <BrandMark size={72} className="pointer-events-none absolute -bottom-4 -right-3 opacity-25" />}
    </button>
  );
};

const QuickTile = ({ icon: Icon, label, sub, onClick, testid, primary }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={cn(
      "pressable flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
      primary
        ? "border-transparent bg-primary text-primary-foreground hover:bg-primary/90"
        : "border-border bg-card hover:bg-accent"
    )}
  >
    <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", primary ? "bg-black/10" : "bg-muted")}>
      <Icon className="h-5 w-5" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold">{label}</span>
      {sub && <span className={cn("block text-xs", primary ? "opacity-75" : "text-muted-foreground")}>{sub}</span>}
    </span>
    <ChevronRight className="h-4 w-4 opacity-60" />
  </button>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { parts_page_enabled } = useFeatures();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState(null);
  const [taskDialog, setTaskDialog] = useState({ open: false, task: null, defaults: null });
  const taskSummary = useTaskSummary();

  const loadTasks = useCallback(() => {
    fetchTasks({ status: "open", limit: 5 }).then((d) => setTasks(d.items || [])).catch(() => setTasks([]));
  }, []);
  useEffect(() => {
    loadTasks();
    window.addEventListener(TASKS_CHANGED, loadTasks);
    return () => window.removeEventListener(TASKS_CHANGED, loadTasks);
  }, [loadTasks]);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/dashboard"), api.get("/reports")])
      .then(([d, r]) => {
        setData(d.data);
        setReports((r.data || []).slice(0, 5));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const s = data?.summary || {};
  const deadlines = useMemo(() => data?.deadlines || [], [data]);
  const urgent = useMemo(() => deadlines.filter((d) => d.status !== "ok").length, [deadlines]);
  const firstName = (user?.name || "").split(" ")[0];
  const today = format(new Date(), "EEEE d. MMMM yyyy", { locale: nb });

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard">
      {/* Greeting */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="dashboard-greeting">
            {greeting()}{firstName ? `, ${firstName}` : ""}
          </h2>
          <p className="mt-0.5 text-sm capitalize text-muted-foreground">{today}</p>
        </div>
        {!loading && (
          <p className="text-sm text-muted-foreground" data-testid="dashboard-urgent-summary">
            {urgent === 0 ? "Ingen fristar krev handling no." : `${urgent} frist${urgent === 1 ? "" : "ar"} krev oppfølging.`}
          </p>
        )}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
        {loading ? (
          [...Array(4)].map((_, i) => <Skeleton key={i} className="h-[104px] rounded-[var(--radius)]" />)
        ) : (
          <>
            <StatCard icon={Wrench} label="Maskiner" value={s.machines ?? 0} tone="dark" testid="stat-machines" onClick={() => navigate("/maskiner")} hint="i maskinparken" />
            <StatCard icon={AlertTriangle} label="Forfalt" value={s.overdue ?? 0} tone="overdue" testid="stat-overdue" onClick={() => navigate("/maskiner?status=overdue")} hint="krev handling" />
            <StatCard icon={Clock} label="Nærmar seg" value={s.upcoming ?? 0} tone="upcoming" testid="stat-upcoming" onClick={() => navigate("/maskiner?status=upcoming")} hint="innan 30 dagar" />
            <StatCard icon={CheckCircle2} label="OK" value={s.ok ?? 0} tone="ok" testid="stat-ok" onClick={() => navigate("/maskiner?status=ok")} hint="innanfor frist" />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        {/* Deadlines */}
        <Card className="min-w-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="font-heading text-base font-bold">Fristar – prioritert</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/kalender")} data-testid="dashboard-open-calendar">
              <CalendarClock /> Kalender
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
            ) : deadlines.length === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="Ingen fristar registrert"
                description="Legg inn maskiner med service- eller årskontroll-datoar for å sjå fristar her."
                action={<Button onClick={() => navigate("/maskiner?ny=1")}><Plus /> Ny maskin</Button>}
              />
            ) : (
              <div className="-mx-2 divide-y divide-border" data-testid="dashboard-deadline-list">
                {deadlines.slice(0, 10).map((d, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => navigate(`/maskiner/${d.machine_id}`)}
                    data-testid={`deadline-row-${i}`}
                    className="row-hover flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left hover:bg-accent"
                  >
                    <KindIcon kind={d.kind} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">{d.label}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {kindLabel(d.kind)} · {d.customer_name || "Ukjend kunde"}
                        <span className="sm:hidden"> · {formatDate(d.due_date)}</span>
                      </div>
                    </div>
                    <div className="hidden text-right text-xs text-muted-foreground sm:block">
                      <div className="font-semibold tabular-nums text-foreground">{formatDate(d.due_date)}</div>
                      <div>{daysText(d.days_remaining)}</div>
                    </div>
                    <StatusPill status={d.status} />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-6">
          {/* Quick actions */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base font-bold">Snarvegar</CardTitle></CardHeader>
            <CardContent className="space-y-2 pt-0">
              <QuickTile primary icon={Mic} label="Snakk inn rapport" sub="Ta opp – AI fyller ut felta for deg" onClick={() => navigate("/rapportar/ny-service?voice=1")} testid="quick-voice-report" />
              <QuickTile icon={FileText} label="Ny servicerapport" sub="Oljeskift, filter, årskontroll" onClick={() => navigate("/rapportar/ny-service")} testid="quick-new-report" />
              <QuickTile icon={ClipboardList} label="Ny jobbrapport" sub="Reparasjon eller anna jobb" onClick={() => navigate("/rapportar/ny-jobb")} testid="quick-new-jobb" />
              <QuickTile icon={Wrench} label="Ny maskin" onClick={() => navigate("/maskiner?ny=1")} testid="quick-new-machine" />
              <QuickTile icon={Users} label="Ny kunde" onClick={() => navigate("/kunder?ny=1")} testid="quick-new-customer" />
            </CardContent>
          </Card>

          {/* Huskeliste */}
          <Card className="shadow-sm" data-testid="dashboard-tasks">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="flex items-center gap-2 font-heading text-base font-bold">
                <ListTodo className="h-4 w-4 text-primary" /> Huskeliste
                {taskSummary?.open > 0 && <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums" data-testid="dashboard-tasks-open-count">{taskSummary.open}</span>}
                {taskSummary?.overdue > 0 && <span className="status-overdue rounded-full border px-1.5 text-[11px] font-semibold tabular-nums" data-testid="dashboard-tasks-overdue-count">{taskSummary.overdue} over tida</span>}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/huskeliste")} data-testid="dashboard-all-tasks">
                Alle <ArrowRight />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <QuickAddTask placeholder="Legg til noko som må hugsast …" testid="dashboard-task-quick-add" onMore={(title) => setTaskDialog({ open: true, task: null, defaults: { title } })} />
              {tasks === null ? (
                <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
              ) : tasks.length === 0 ? (
                <p className="px-1 text-sm text-muted-foreground" data-testid="dashboard-tasks-empty">Ingenting på huskelista. Skriv inn det første punktet over.</p>
              ) : (
                <div className="-mx-1 divide-y divide-border">
                  {tasks.map((t) => (
                    <TaskItem key={t.id} task={t} compact onEdit={(task) => setTaskDialog({ open: true, task, defaults: null })} onChange={loadTasks} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent reports */}
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="font-heading text-base font-bold">Siste rapportar</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/rapportar")} data-testid="dashboard-all-reports">
                Alle <ArrowRight />
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
              ) : reports.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen rapportar enno.</p>
              ) : (
                <div className="-mx-2 divide-y divide-border">
                  {reports.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => navigate(`/rapportar?open=${r.id}`)}
                      data-testid={`recent-report-${r.id}`}
                      className="row-hover flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-accent"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">
                          {r.report_number} · {r.title || r.service_type || (r.report_type === "jobb" ? "Jobb" : "Service")}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {formatDate(r.date)} · {r.customer_snapshot?.name || "—"}
                        </div>
                      </div>
                      <ReportTypeBadge type={r.report_type} />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Overview */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base font-bold">Oversikt</CardTitle></CardHeader>
            <CardContent className="space-y-2 pt-0 text-sm">
              <div className="flex items-center justify-between py-1">
                <span className="flex items-center gap-2 text-muted-foreground"><Users className="h-4 w-4" /> Kundar</span>
                <span className="font-semibold tabular-nums" data-testid="overview-customers">{loading ? "–" : s.customers ?? 0}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="flex items-center gap-2 text-muted-foreground"><FileText className="h-4 w-4" /> Rapportar</span>
                <span className="font-semibold tabular-nums" data-testid="overview-reports">{loading ? "–" : s.reports ?? 0}</span>
              </div>
              {parts_page_enabled ? (
                <Button onClick={() => navigate("/deler")} variant="ghost" className="w-full justify-between px-2" data-testid="dashboard-open-orderlist">
                  <span className="flex items-center gap-2"><Package className="h-4 w-4" /> Bestillingsliste</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button onClick={() => navigate("/kunder")} variant="ghost" className="w-full justify-between px-2" data-testid="dashboard-open-customers">
                  <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Kundar og maskinpark</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <TaskDialog open={taskDialog.open} onOpenChange={(o) => setTaskDialog((d) => ({ ...d, open: o }))} task={taskDialog.task} defaults={taskDialog.defaults} onSaved={loadTasks} />
    </div>
  );
}
